import {
    attributeBatchSupplier,
    getCmSites,
    getSiteBatchRegister,
    getSiteCatalog,
    getSiteLedgerForBrand,
    getSiteOrdersForBrand,
    getSiteStock,
    reverseOnBehalf,
} from '@api/cm-sites';
import type {
    BatchRegisterRow,
    BucketedBatchLine,
    BucketedItemBalance,
    SiteLedgerEntry,
} from '@api/vendor-portal';
import { ArrowLeft, ChartBar, Plus } from '@phosphor-icons/react';

import PageHeader from '@components/common/PageHeader';
import { buildLedgerRows, type LedgerRow, reasonText } from '@components/containers/vendorHistory/ledgerRows';
import OpenOrdersBanner from '@components/containers/vendorHome/OpenOrdersBanner';
import {
    buildOrderTotals,
    openOrdersOnly,
} from '@components/containers/vendorHome/orderTotals';

import { formatMoney } from '@components/containers/procurementOrders/shared';

import {
    buildSiteProductionRows,
    filterSiteProductionRows,
    toSkuSiteProductionRows,
    type SiteProductionRow,
} from './siteProductionRows';
import { resolveCancelAction } from '@components/containers/workOrders/productionOrderRows';
import { useCancelProduction } from '@components/containers/workOrders/useCancelProduction';
import {
    DataSheet,
    type DataSheetColumn,
    type DataSheetFilterConfig,
    type FilterValue,
    SheetTabStrip,
} from '@components/ui/DataSheet';
import StatCard from '@components/ui/StatCard';
import StatusPill, { type StatusTone } from '@components/ui/StatusPill';
import useToaster from '@hooks/toaster/useToaster';
import usePersistentState from '@hooks/usePersistentState';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { DateRangePicker, type UiDateRange } from '@components/ui/DateRange';
import { hasDayRange, inDayRange, NO_DAY_RANGE } from '@utils/productionWindow';
import { formatNumber } from '@utils/materialQuantity';
import ReceiptCostModal, { type CostableReceipt } from '@components/containers/productionPlanning/ReceiptCostModal';
import {
    Button,
    Group,
    Modal,
    SegmentedControl,
    Select,
    Stack,
    Text,
    Textarea,
    Tooltip,
} from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import TraceDrawer from '@components/containers/trace/TraceDrawer';
import BatchPanel from '@components/containers/vendorPortal/BatchPanel';
import BatchDetailPanel from '@components/containers/vendorStock/BatchDetailPanel';
import { useStockColumns } from '@components/containers/vendorStock/useStockColumns';

import AdjustStockModal, { type AdjustTarget } from './AdjustStockModal';
import InviteLoginModal from './InviteLoginModal';

import BatchRegister from '@components/containers/vendorPortal/BatchRegister';
import ShipmentBuilderModal from '@components/containers/vendorPortal/ShipmentBuilderModal';
import { summarise } from '@components/containers/vendorPortal/shipmentRows';
import ProductDispatch, {
    DispatchViewToggle,
} from '@components/containers/vendorPortal/ProductDispatch';
import { useDispatchSelection } from '@components/containers/vendorPortal/useDispatchSelection';
import DeclareBatchesModal from '@components/containers/vendorPortal/DeclareBatchesModal';
import { toOrderLines } from '@components/containers/vendorPortal/batchGridRows';
import ReportedByField from '@components/containers/vendorPortal/ReportedByField';

import VendorReceiveContainer from '@components/containers/vendorReceive';
import { tracesAnything } from '@components/containers/vendorReceive/rows';
import WorkOrderStatusPill, {
    STATUS_LABEL,
} from '@components/containers/workOrders/StatusPill';

import pageStyles from './SiteDetail.module.css';
import { formatCount } from '@utils/currency';

type Tab = 'stock' | 'production' | 'batches' | 'qc' | 'dispatch' | 'history' | 'receive';

const TABS = [
    { id: 'stock', label: 'Stock' },
    { id: 'production', label: 'Production' },
    // The site's runs, made through to dispatched. The same register the manufacturer reads,
    // because the first thing a brand does with it is check it against what the site sent them.
    { id: 'batches', label: 'Batches' },
    // The verdict worklist: every batch still awaiting QC, either kind. A row leaves the
    // moment a verdict lands, so an empty table is the goal state. Sits before Dispatch
    // because that is the order the work moves through - made, judged, sent.
    { id: 'qc', label: 'QC Pending' },
    // The queue for whoever owns dispatch: what is still here and could yet go. Batches keeps
    // every run as the ledger; this is its complement, so a row leaves when nothing remains.
    { id: 'dispatch', label: 'Dispatch' },
    { id: 'history', label: 'History' },
    // The site's own grid, unchanged, writing through the brand's door.
    { id: 'receive', label: 'Receive' },
];

const PROVENANCE: Record<LedgerRow['provenance'], { label: string; tone: StatusTone }> = {
    CM: { label: 'Site entered', tone: 'positive' },
    BRAND: { label: 'You entered', tone: 'info' },
    OPENING: { label: 'Opening', tone: 'warning' },
    SYNC: { label: 'System', tone: 'neutral' },
};

/**
 * One manufacturing site, as the brand sees it.
 *
 * THE SAME THREE VIEWS THE SITE HAS, reading through the same services, because the first thing
 * a brand does with this screen is check it against what the site told them on the phone — and
 * a second reader over one set of numbers would eventually disagree with the first.
 *
 * WRITING HAPPENS BEHIND THE BANNER. The brand never borrows the site's login: it stays in its
 * own session, names the site, and says whose word the numbers are. That is what keeps the
 * ledger able to answer "who typed this" and "who said it" separately, forever.
 *
 * A BRAND REVERSAL IS NEVER SILENT. It demands a reason, it is attributed, and it appears in
 * the site's own history marked as the brand's doing. A correction the site cannot see is a
 * correction it will argue with — and the argument would be about the ledger's honesty rather
 * than about the number.
 */
const SiteDetailContainer: FC = () => {
    const { warehouseId = '' } = useParams();
    const navigate = useNavigate();
    const { showToastSuccess, showToastError } = useToaster();

    /**
     * The open tab lives in the URL.
     *
     * So a link can point at one — the sites list sends somebody straight to Receive when the
     * job is to key in what a quiet site reported by phone, rather than landing them on a stock
     * screen they then have to navigate out of. It also means a tab can be shared: "look at
     * their history" becomes a link rather than an instruction.
     */
    const [params, setParams] = useSearchParams();

    /**
     * Read for two things: whether this brand traces the item being corrected by batch, which
     * decides if the adjustment form demands a batch, and whether it traces anything at all,
     * which decides if the batch tabs are shown. Cheap, cached, and the same call the site's
     * own screens make.
     */
    const { data: catalog } = useQuery(
        ['cm-site-catalog', warehouseId],
        () => getSiteCatalog(warehouseId),
        { enabled: !!warehouseId, staleTime: 5 * 60 * 1000 },
    );

    // Batches and QC Pending list batches, so a brand that tracks none has nothing to show in
    // them. Kept until the catalog says so, so a brand that does never loses them mid-load.
    const tabs = catalog && !tracesAnything(catalog)
        ? TABS.filter((entry) => entry.id !== 'batches' && entry.id !== 'qc')
        : TABS;
    const tab = (tabs.some((entry) => entry.id === params.get('tab'))
        ? params.get('tab')
        : 'stock') as Tab;

    const setTab = (next: Tab) => setParams(next === 'stock' ? {} : { tab: next });
    const [stockView, setStockView] = useState<'material' | 'product'>('material');
    // QC pending, Rejected and a batch count can only ever read nought for a kind nobody batches.
    const stockTracked =
        !catalog ||
        (stockView === 'product' ? ['FG'] : ['RM', 'PM']).some(
            (scope) => catalog.batchTracking?.[scope] === 'full',
        );
    /**
     * Separate from stockView on purpose. Stock and the register answer different questions, and
     * somebody checking finished runs against a manufacturer's spreadsheet should not have their
     * place changed because they last looked at materials on the Stock tab.
     */
    const [registerKind, setRegisterKind] = useState<'product' | 'material'>('product');
    /**
     * The batch being ruled on. Quality for finished goods is the brand's call, and this is
     * where they make it — beside the register that lists what is waiting.
     */
    const [selectedBatch, setSelectedBatch] = useState<BatchRegisterRow | null>(null);
    /** A batch's history, opened from either panel. Both are drawers, so this replaces them. */
    const [historyBatchId, setHistoryBatchId] = useState<string | null>(null);
    /** No ticks is every status, which is what the tab opens on. */
    const [statusFilter, setStatusFilter] = useState<FilterValue>({});
    /**
     * Memoised, not derived inline: `?? []` mints a new array every render, and this feeds the
     * dependency list of the memo that filters the grid — so the whole tree would re-filter on
     * every keystroke somewhere else on the page.
     */
    const productionStatus = useMemo(
        () => statusFilter.status ?? [],
        [statusFilter],
    );
    /**
     * The window of order dates on screen. Empty until somebody picks one.
     *
     * A SECOND CUT, NOT A REPLACEMENT for the status filter beside it: "what did we place in
     * August" and "what is still open" are different questions, and this tab is where both get
     * asked. They compose — status first, then dates.
     */
    const [orderedRange, setOrderedRange] = useState<UiDateRange>(NO_DAY_RANGE);
    /** A stock row opened for its per-batch breakdown — the same drawer the site sees. */
    const [stockPanelItem, setStockPanelItem] = useState<BucketedItemBalance | null>(null);
    /** The item being corrected, and the batch if the correction started from one. */
    const [adjusting, setAdjusting] = useState<{
        item: AdjustTarget;
        batch?: BucketedBatchLine | null;
    } | null>(null);
    /** Landing here from the sites list pre-filters to what is still awaiting a verdict. */
    const [qcFilter, setQcFilter] = useState<string | null>(
        params.get('qc') ?? null,
    );
    const [reportedBy, setReportedBy] = useState('');
    /** Landing here from the sites list narrows the history to receipts still owed a supplier. */
    const [onlyAwaitingSupplier, setOnlyAwaitingSupplier] = useState(
        params.get('supplier') === 'waiting',
    );
    /** The batch whose supplier is being saved, so its row shows the wait and nothing else does. */
    const [attributing, setAttributing] = useState<string | null>(null);
    const [reversing, setReversing] = useState<LedgerRow | null>(null);
    const [reverseReason, setReverseReason] = useState('');
    const [isReversing, setIsReversing] = useState(false);
    const [declaringMany, setDeclaringMany] = useState(false);
    /**
     * The batches ticked for a shipment.
     *
     * Held here rather than inside the register, so leaving the tab discards it. A selection
     * surviving a tab change is a shipment somebody forgot they had started, and the next thing
     * they do is send a lorry they did not mean to.
     */
    const [isInviting, setIsInviting] = useState(false);

    /**
     * Only once the coordinator has said whose word it is.
     *
     * The name is not optional decoration: a row entered by the brand with nobody named is a
     * row the site cannot recognise as its own report, and the first thing it will do is
     * dispute it.
     */
    /**
     * We are on a site's page, so we are always acting for that site — the question is only
     * who at the site reported the numbers, and that is now asked inside each form that
     * writes. Previously this was null until a banner was filled in, which left the Record
     * button greyed and the Receive tab replaced by a warning on arrival.
     */
    const onBehalfOf = useMemo(
        () => ({ warehouseId, reportedBy: reportedBy.trim() }),
        [warehouseId, reportedBy],
    );

    const { data: sitesData } = useQuery(['cm-sites'], () => getCmSites());
    const site = sitesData?.sites.find((entry) => entry.warehouseId === warehouseId);

    const {
        data: stockData,
        isLoading: stockLoading,
        refetch: refetchStock,
    } = useQuery(
        ['cm-site-stock', warehouseId, stockView],
        () => getSiteStock(warehouseId, { itemType: stockView }),
        { enabled: !!warehouseId && tab === 'stock', keepPreviousData: true },
    );

    /**
     * NOT GATED ON THE PRODUCTION TAB, unlike its neighbours.
     *
     * The workload banner sits above the tabs and has to be right whichever one is open, so this
     * is the one query here that must run regardless. It is a single site's open orders — small,
     * and cached by React Query across tab switches, so the cost is one request per visit rather
     * than one per tab.
     */
    const {
        data: ordersData,
        isLoading: ordersLoading,
        refetch: refetchOrders,
    } = useQuery(
        ['cm-site-orders', warehouseId],
        () => getSiteOrdersForBrand(warehouseId),
        { enabled: !!warehouseId },
    );

    // The same confirm-and-cancel the brand's Production Orders grid uses, so the wording a
    // coordinator reads here is the wording they read there.
    const { requestCancel, cancellingKey } = useCancelProduction(() => refetchOrders());

    /*
     * The same arithmetic the site sees on its own home screen, so the two cannot report
     * different workloads for the same orders.
     *
     * Narrowed to open lines first, which the site's own screen gets for free because its queue
     * never fetches anything else. This one fetches the whole book so the grid below can show
     * it, and a banner counting shipped and cancelled work as "still to send" would be wrong in
     * the direction that matters.
     */
    const orderTotals = useMemo(
        () => buildOrderTotals(openOrdersOnly(ordersData?.orders ?? [])),
        [ordersData],
    );

    /**
     * The site's book, grouped by the order it was commissioned under.
     *
     * ORDER-GRAINED, NOT LINE-GRAINED, because that is the question this tab is opened with:
     * how is that order going, in total. Line by line, a three-SKU order is three rows to add up
     * by eye before it says anything.
     */
    const productionRows = useMemo(
        () => buildSiteProductionRows(ordersData?.orders ?? []),
        [ordersData],
    );

    /*
     * Offered in lifecycle order and only where the site actually has one.
     *
     * Driven off STATUS_LABEL's own key order, so the dropdown reads Issued, In Production,
     * Ready to Ship, Shipped rather than alphabetically - the same order the pill's map is
     * written in, and the order the work moves through.
     *
     * Built from the rows rather than the enum so nobody picks a status with nothing behind it
     * and reads an empty grid as a fault.
     */
    const productionStatusOptions = useMemo(() => {
        const present = new Set(
            productionRows.flatMap((row) => [
                String(row.status),
                ...(row.lines ?? []).map((line) => String(line.status)),
            ]),
        );
        // No synthetic "All" row: nothing ticked already means every status, and an option
        // competing with that would let somebody pick "All" AND "Shipped" at once.
        return Object.keys(STATUS_LABEL)
            .filter((status) => present.has(status))
            .map((status) => ({ value: status, label: STATUS_LABEL[status] }));
    }, [productionRows]);

    /**
     * THE HEADER BAR'S OWN FILTER, so this reads like every other filter in the app.
     *
     * It was a bare Mantine Select, which sat in a row of these controls in a different shape
     * and a different typeface — and could only ask about one status at a time, when the
     * question here is usually a union: "what is still moving" is issued and in production.
     */
    const statusFilterConfig: DataSheetFilterConfig = {
        label: 'Status',
        params: [
            {
                key: 'status',
                label: 'Status',
                mode: 'multi',
                options: productionStatusOptions,
            },
        ],
        value: statusFilter,
        onChange: setStatusFilter,
    };

    /**
     * Status first, then the calendar.
     *
     * THE DATE CUT IS AT THE ORDER LEVEL, and its lines ride along. `orderDate` is a fact about
     * the header — a line is neither placed nor priced separately, so every child carries null
     * — which means filtering row by row would keep an order and throw away all of its SKUs.
     *
     * A plain filter, unlike the status one, because it never drops SOME of an order's lines:
     * the whole order is in the window or it is not, so no parent total has to be re-summed.
     */
    const visibleProductionRows = useMemo(() => {
        const byStatus = filterSiteProductionRows(productionRows, productionStatus);

        return hasDayRange(orderedRange)
            ? byStatus.filter((row) => inDayRange(row.orderDate, orderedRange))
            : byStatus;
    }, [productionRows, productionStatus, orderedRange]);

    /**
     * Whether the sheet is grouped by product rather than by order.
     *
     * THE SAME CONTROL, THE SAME KEY SHAPE AND THE SAME DEFAULT as the site's own Production
     * Orders page, because this is the same list seen from the other side — a grouping that
     * existed on only one of them would be a difference to explain in every conversation about
     * what the two screens show.
     */
    const [productionGroupBy, setProductionGroupBy] = usePersistentState<string | null>(
        'reparo.siteProduction.groupBy',
        null,
    );
    const isSkuGrouped = productionGroupBy === 'sku';

    /*
     * FOLDED LAST, after both filters. The status filter narrows LINES and re-sums the header
     * above them; grouping what survived keeps that rule in one place rather than teaching it
     * about a second shape.
     */
    const displayedProductionRows = useMemo(
        () =>
            isSkuGrouped
                ? toSkuSiteProductionRows(visibleProductionRows)
                : visibleProductionRows,
        [visibleProductionRows, isSkuGrouped],
    );

    /** Lines rather than orders, because that is what the count on the card has always meant. */
    const lineCount = (rows: SiteProductionRow[]) =>
        rows.reduce((total, row) => total + (row.lines?.length ?? 1), 0);

    /**
     * Called out rather than swallowed: a run commissioned without an order header has no order
     * date at all, so a window silently drops it. Saying so is the difference between a filter
     * and a screen that looks like it has lost work.
     */
    const undatedOrders = (() => {
        if (!hasDayRange(orderedRange)) return '';
        const missing = filterSiteProductionRows(
            productionRows,
            productionStatus,
        ).filter((row) => !row.orderDate).length;

        return missing > 0 ? `, ${missing} with no order date hidden` : '';
    })();

    // Cost is the brand's commercial data. This screen is brand-side, but a consultant can
    // reach it too, so the column is gated rather than assumed.
    const { currentUser } = useCurrentUser();
    const canSeeCost = currentUser?.role === 'ADMIN';
    const [costing, setCosting] = useState<CostableReceipt | null>(null);

    const {
        data: ledgerData,
        isLoading: ledgerLoading,
        refetch: refetchLedger,
    } = useQuery(
        ['cm-site-ledger', warehouseId],
        () => getSiteLedgerForBrand(warehouseId, { pageSize: 100 }),
        { enabled: !!warehouseId && tab === 'history' },
    );

    // Part of the cache key, not just the request: the two kinds are different lists and
    // sharing one entry would show yarn batches under Finished goods for a beat after a toggle.
    const {
        data: register,
        isLoading: registerLoading,
        refetch: refetchRegister,
    } = useQuery(
        // Dispatch is products only — materials are consumed, not sent — so it pins the kind
        // rather than inheriting whichever one the Batches tab was last showing.
        ['cm-site-batches', warehouseId, tab === 'dispatch' ? 'product' : registerKind],
        () =>
            getSiteBatchRegister(
                warehouseId,
                tab === 'dispatch' ? 'product' : registerKind,
            ),
        { enabled: !!warehouseId && (tab === 'batches' || tab === 'qc' || tab === 'dispatch') },
    );

    /**
     * The ticked rows, back in full.
     *
     * The set holds ids so the checkboxes stay cheap; the builder needs the whole row — code,
     * item name, and what is still on the shelf. Resolved against the LIVE register rather than
     * remembered from when they were ticked, so a refetch between picking and sending shows the
     * real remaining quantity rather than a stale one.
     */
    /**
     * Whether a correction to this item has to name a batch.
     *
     * The config splits RM / PM / FG while an item only says product or material, so a material
     * is resolved to its own scope through the catalog. Same rule the receive grid uses; a
     * product is always FG.
     */
    const isBatchRequired = (item: AdjustTarget | null): boolean => {
        if (!item) return false;
        if (item.itemType === 'product') return catalog?.batchTracking?.FG === 'full';

        const material = catalog?.materials.find((entry) => entry.itemId === item.itemId);
        return !!material && catalog?.batchTracking?.[material.materialType] === 'full';
    };

    /** The dispatch queue: still here, could yet go, soonest expiry first. */
    const {
        queue,
        groups,
        view: dispatchView,
        setView: setDispatchView,
        pickedBatches,
        building,
        setBuilding,
        clear: clearPicked,
        toggleProduct,
        toggleAllProducts,
        isProductPicked,
        selection,
    } = useDispatchSelection(register?.batches);


    const items = useMemo(() => stockData?.items ?? [], [stockData]);
    const ledgerRows = useMemo(
        () => buildLedgerRows((ledgerData?.entries ?? []) as SiteLedgerEntry[]),
        [ledgerData],
    );
    const awaitingSupplierRows = useMemo(
        () => ledgerRows.filter((row) => row.awaitingSupplier),
        [ledgerRows],
    );
    // Only while there is something to narrow to. Attributing the last one empties the list,
    // and a filter left on then showed a blank history with nothing to switch it off.
    const showOnlyAwaiting = onlyAwaitingSupplier && awaitingSupplierRows.length > 0;

    const supplierName = (vendorId: string) =>
        catalog?.suppliers.find((supplier) => supplier.vendorId === vendorId)?.name ?? vendorId;

    /**
     * The brand saying who sent a batch the site received without naming anyone.
     *
     * One dropdown on the row, mapped suppliers only. The batch keeps its id, so the ledger
     * simply re-reads with the row no longer flagged; the sites list picks up the count on
     * its next load.
     */
    const attributeSupplier = async (row: LedgerRow, supplierId: string) => {
        if (!row.batchId) return;
        setAttributing(row.batchId);
        try {
            await attributeBatchSupplier(warehouseId, row.batchId, supplierId);
            showToastSuccess({
                title: `${row.batchCode ?? 'Batch'} attributed to ${supplierName(supplierId)}`,
                subtitle: 'The batch keeps its id; every movement on it still points at it.',
            });
            void refetchLedger();
        } catch (err: any) {
            showToastError({
                title: 'Could not record the supplier',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setAttributing(null);
        }
    };

    /** Only who the brand has mapped to THIS material - the same list its receive picker offers. */
    const supplierOptionsFor = (itemId: string) => {
        const mapped = catalog?.materials.find((material) => material.itemId === itemId)?.supplierIds ?? [];
        return mapped.map((vendorId) => ({ value: vendorId, label: supplierName(vendorId) }));
    };

    const totals = useMemo(
        () =>
            items.reduce(
                (acc, item) => ({
                    usable: acc.usable + item.usable,
                    inQc: acc.inQc + item.inQc,
                    blocked: acc.blocked + item.blocked,
                }),
                { usable: 0, inQc: 0, blocked: 0 },
            ),
        [items],
    );

    const handleReverse = async () => {
        if (!reversing?.reversibleEventId) return;
        setIsReversing(true);
        try {
            const result = await reverseOnBehalf(
                { warehouseId, reportedBy: reportedBy.trim() },
                reversing.reversibleEventId,
                reverseReason.trim(),
            );
            showToastSuccess({
                title: 'Reversed',
                subtitle:
                    result.reversedCount > 1
                        ? `${result.reversedCount} movements were reversed together — a run or a delivery cannot be half undone. The site sees this and the reason.`
                        : 'The site sees this in their own history, with your reason attached.',
            });
            setReversing(null);
            setReverseReason('');
            void refetchLedger();
        } catch (err: any) {
            showToastError({
                title: 'Could not reverse it',
                subtitle: err?.error?.message ?? err?.message ?? 'Something went wrong.',
            });
        } finally {
            setIsReversing(false);
        }
    };

    /** Balances read to one decimal; see the Manufactured column. */
    const qty = (value: number | null | undefined) => formatNumber(value ?? 0, 1);

    const stockColumns = useMemo<DataSheetColumn<BucketedItemBalance>[]>(
        () => [
            {
                key: 'itemId',
                header: 'Item',
                width: 260,
                sticky: true,
                filterable: true,
                render: (value: string, row) => row.itemName ?? value,
                valueAccessor: (row) => row.itemName ?? row.itemId,
                csvValue: (value: string, row) => row.itemName ?? value,
            },
            /*
             * THE ONE FLOW ON A SHEET OF BALANCES, so it leads rather than sits among them.
             *
             * Everything to its right is what is here now; this is what has ever come off the
             * line. Reading left to right therefore runs made → what became of it, which is the
             * order the question gets asked in, and it is why the column cannot be checked
             * against its neighbours: a batch that shipped last month still counts here and
             * counts nowhere else on the row.
             *
             * Relabelled for material, exactly as the batch register does — a drum is received,
             * not manufactured, and one header for both would be wrong for one of them.
             */
            {
                key: 'manufactured',
                header: stockView === 'product' ? 'Manufactured' : 'Received',
                width: 130,
                align: 'right',
                sortable: true,
                // One decimal at most: a drum of flavour is 600.5 kg, not 600.5360000001, and a
                // whole number stays whole. The export keeps the raw figure.
                render: (value: number | undefined) => formatNumber(value ?? 0, 1),
                valueAccessor: (row) => row.manufactured ?? 0,
                csvValue: (value: number | undefined) => value ?? 0,
            },
            { key: 'usable', header: 'QC approved', width: 130, align: 'right', sortable: true, render: qty },
            { key: 'inQc', header: 'QC pending', width: 120, align: 'right', sortable: true, render: qty },
            { key: 'blocked', header: 'Rejected', width: 110, align: 'right', sortable: true, render: qty },
            { key: 'total', header: 'On Hand', width: 110, align: 'right', sortable: true, render: qty },
            {
                key: 'batches',
                header: 'Batches',
                width: 90,
                align: 'right',
                render: (_value, row) => row.batches.length,
                valueAccessor: (row) => row.batches.length,
                csvValue: (_value, row) => row.batches.length,
            },
        ],
        [stockView],
    );

    const stockGrid = useStockColumns(stockColumns, {
        tracked: stockTracked,
        storageKey: 'reparo.siteStock.hiddenCols',
    });

    const ledgerColumns = useMemo<DataSheetColumn<LedgerRow>[]>(
        () => [
            { key: 'date', header: 'Date', width: 110, sticky: true },
            ...(canSeeCost
                ? [{
                    key: 'unitCost',
                    header: 'Rate paid',
                    width: 130,
                    render: (_value: unknown, row: LedgerRow) => {
                        if (row.unitCost !== null) {
                            return (
                                <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                                    {row.currency ?? 'INR'}{' '}
                                    {formatCount(row.unitCost, { decimals: 2 })}
                                </Text>
                            );
                        }
                        if (!row.isReceipt) {
                            return <Text fz={13} c={neutralColors.textSecondary}>--</Text>;
                        }
                        return (
                            <Button
                                size="compact-xs"
                                variant={ButtonVariant.DEFAULT}
                                onClick={() => setCosting({
                                    id: row.reversibleEventId ?? row.key,
                                    materialId: row.itemId,
                                    materialName: row.itemName,
                                    quantity: Math.abs(row.quantity),
                                    date: row.date,
                                })}
                            >
                                Add cost
                            </Button>
                        );
                    },
                } as DataSheetColumn<LedgerRow>]
                : []),
            { key: 'label', header: 'What happened', width: 170, filterable: true },
            // A delivery's goods receipt number and the order it came on. Blank on everything
            // that is not a receipt, and on receipts recorded before numbering began.
            {
                key: 'grnId',
                header: 'GRN',
                width: 140,
                filterable: true,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'purchaseOrderId',
                header: 'PO',
                width: 130,
                filterable: true,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'itemId',
                header: 'Item',
                width: 220,
                filterable: true,
                render: (value: string, row) => row.itemName ?? value,
                valueAccessor: (row) => row.itemName ?? row.itemId,
                csvValue: (value: string, row) => row.itemName ?? value,
            },
            {
                key: 'batchCode',
                header: 'Batch',
                width: 130,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'awaitingSupplier',
                header: 'Supplier',
                width: 200,
                csvValue: (value: boolean | undefined) => (value ? 'to be recorded' : ''),
                // Only a receipt the site filed under itself has anything to say here: the
                // dropdown IS the attribution, saved on pick. Every other row is quiet.
                render: (value: boolean | undefined, row) =>
                    value && row.batchId ? (
                        <Select
                            size="xs"
                            placeholder="Who sent it"
                            data={supplierOptionsFor(row.itemId)}
                            value={null}
                            disabled={attributing === row.batchId}
                            onChange={(picked) => picked && void attributeSupplier(row, picked)}
                            searchable
                        />
                    ) : null,
            },
            {
                key: 'quantity',
                header: 'Quantity',
                width: 100,
                align: 'right',
                render: (value: number) => (
                    <Text
                        fz={13}
                        c={value < 0 ? semanticColors.critical.primary : neutralColors.textPrimary}
                    >
                        {value > 0 ? `+${value}` : value}
                    </Text>
                ),
            },
            {
                key: 'reasonCode',
                header: 'Reason',
                width: 220,
                filterable: true,
                render: (_value: string | null, row) => reasonText(row) ?? '—',
                valueAccessor: (row) => reasonText(row) ?? '',
                csvValue: (_value: string | null, row) => reasonText(row) ?? '',
            },
            {
                key: 'enteredByName',
                header: 'Reported by',
                width: 150,
                render: (value: string | null) => value ?? '—',
            },
            {
                key: 'provenance',
                header: 'Source',
                width: 130,
                render: (value: LedgerRow['provenance']) => (
                    <StatusPill label={PROVENANCE[value].label} tone={PROVENANCE[value].tone} />
                ),
                csvValue: (value: LedgerRow['provenance']) => PROVENANCE[value].label,
            },
            {
                key: '_reverse',
                header: '',
                width: 110,
                csvValue: () => '',
                render: (_value, row) => {
                    if (row.isChild || row.reversalOfId) return null;
                    if (row.reversedByEventId) return <StatusPill label="Reversed" tone="critical" />;
                    // No 24-hour test: the brand finds bad data late by nature, and the window
                    // exists for the site's own typos.
                    if (row.provenance === 'SYNC') return null;

                    return (
                        <Button
                            size="xs"
                            variant={ButtonVariant.SUBTLE}
                            onClick={() => setReversing(row)}
                        >
                            Reverse
                        </Button>
                    );
                },
            },
        ],
        // The supplier column reads the catalog and the in-flight save; the handlers above are
        // plain closures over state this memo already lists, so they are not deps themselves.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [canSeeCost, catalog, attributing],
    );

    return (
        <div className={`flex h-full w-full flex-col page-shell ${pageStyles.shell}`}>
            <PageHeader
                className={pageStyles.header}
                icon="🏭"
                title={site?.name ?? 'Site'}
                subtitle={site?.vendorName ?? 'Manufacturing site'}
                actions={
                    <Group gap={8}>
                        {/*
                          * The site is taken from the page, so by the time the modal opens the
                          * scope of the resulting login is already decided.
                          */}
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            onClick={() => setIsInviting(true)}
                        >
                            Invite login
                        </Button>
                        {/*
                          * WHAT IS PICKED, BESIDE THE BUTTON THAT ACTS ON IT. This used to sit
                          * under the grid, which on a full page of batches is below the fold —
                          * so the count was invisible at exactly the moment somebody had just
                          * finished ticking rows and was looking for what to do next.
                          */}
                        {tab === 'dispatch' && (
                            <>
                                <Text fz={13} c={neutralColors.textSecondary}>
                                    {pickedBatches.length > 0
                                        ? summarise(
                                              pickedBatches.map((batch) => ({
                                                  quantity: String(batch.remaining),
                                              })),
                                          )
                                        : 'Tick batches to send'}
                                </Text>
                                {pickedBatches.length > 0 && (
                                    <Button
                                        variant={ButtonVariant.SUBTLE}
                                        onClick={clearPicked}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </>
                        )}
                        <Button
                            size="xs"
                            variant={ButtonVariant.DEFAULT}
                            leftSection={<ArrowLeft size={14} />}
                            onClick={() => navigate('/cm-sites')}
                        >
                            All sites
                        </Button>
                        {/*
                          * The read half of the pair beside it. Opened from here it lands
                          * already scoped to this manufacturer, so the same screen answers
                          * "what did they make" without asking which they.
                          */}
                        <Button
                            size="xs"
                            variant={ButtonVariant.DEFAULT}
                            leftSection={<ChartBar size={14} />}
                            onClick={() =>
                                navigate(`/production-monitor?site=${warehouseId}`)
                            }
                        >
                            Monitor production
                        </Button>
                        {/*
                          * The grid form, same as the button of this name in the manufacturer's
                          * own portal. Brand entry is retrospective by nature — a sheet of runs
                          * phoned through after a shift — and the grid handles a single row
                          * perfectly well, so the older one-run-at-a-time form is not offered
                          * here. No longer disabled: who reported the numbers is asked inside.
                          */}
                        <Button
                            size="xs"
                            variant={ButtonVariant.ACTION}
                            leftSection={<Plus size={14} />}
                            onClick={() => setDeclaringMany(true)}
                        >
                            Record production
                        </Button>
                        {/*
                          * SHOWN DISABLED RATHER THAN HIDDEN. An absent button teaches nobody
                          * that shipping is possible from here; a greyed one with the hint above
                          * it says both that the action exists and what it is waiting for.
                          */}
                        {tab === 'dispatch' && (
                            <Button
                                variant={ButtonVariant.ACTION}
                                disabled={pickedBatches.length === 0}
                                onClick={() => setBuilding(true)}
                            >
                                Create shipment
                            </Button>
                        )}
                    </Group>
                }
            />

            {/*
             * ABOVE THE ACTING-FOR STRIP, because it answers a different question and the
             * cheaper one. The strip explains how what you type here is attributed — worth
             * reading once; this says what the site owes and what is waiting on you, which is
             * why the page was opened at all.
             */}
            <OpenOrdersBanner
                totals={orderTotals}
                pendingQc={site?.pendingFgQc}
                onPendingQcClick={() => setParams({ tab: 'batches', qc: 'PENDING' })}
            />

            {/*
              * ONE STRIP, ABOVE WHATEVER THE TAB RENDERS. It used to be passed into each
              * DataSheet, which worked for the four tabs that are grids and left the fifth —
              * Receive, which is a form — hand-rolling its own out of buttons. Two controls
              * doing one job on one screen, and only one of them looked like the app.
              */}
            <SheetTabStrip
                tabs={tabs}
                activeTab={tab}
                onTabChange={(next) => setTab(next as Tab)}
            />

            {tab === 'stock' && (
                <>
                    <Group gap={16} grow align="stretch">
                        <StatCard
                            label="QC approved"
                            value={formatNumber(totals.usable, 1)}
                            helper="Released by QC and in date"
                            valueColor={semanticColors.positive.dark}
                            loading={stockLoading}
                        />
                        <StatCard
                            label="QC pending"
                            value={formatNumber(totals.inQc, 1)}
                            helper="Here, but not yet released"
                            valueColor={semanticColors.warning.dark}
                            loading={stockLoading}
                        />
                        <StatCard
                            label="Rejected"
                            value={formatNumber(totals.blocked, 1)}
                            helper="At the site, not usable"
                            valueColor={semanticColors.critical.primary}
                            loading={stockLoading}
                        />
                    </Group>

                    <DataSheet<BucketedItemBalance>
                        data={items}
                        columns={stockGrid.columns}
                        toolbarActionsRight={stockGrid.picker}
                        title="On Hand"
                        subtitle={stockLoading ? 'Loading…' : `${items.length} items`}
                        getRowKey={(row) => `${row.itemId}-${row.warehouseId}`}
                        /*
                         * The same search the Batches sheet has, and the same one the site's
                         * own stock screen already had. A brand checking a manufacturer's
                         * count reads down this list for ONE item, and on a site carrying a
                         * few hundred SKUs that meant scrolling for it.
                         *
                         * Matches the id as well as the name, because the two sides of a phone
                         * call rarely use the same one.
                         */
                        enableSearch
                        searchPlaceholder="Item…"
                        // The per-batch breakdown behind the row's figures — the same drawer
                        // the site's own Stock screen opens, read-only here.
                        onRowClick={(row) => setStockPanelItem(row)}
                        /*
                         * THE SAME CONTROL THE BATCHES TAB USES, in the same order.
                         *
                         * Two buttons said the wrong thing about the choice: an action button
                         * beside a default one reads as two things you can DO, one of them
                         * highlighted, rather than as one setting with two positions. A person
                         * moving between Stock and Batches was also meeting two different
                         * controls for the identical material/finished-goods question, which is
                         * the sort of difference that gets read as meaning something.
                         */
                        toolbarControls={
                            <SegmentedControl
                                size="xs"
                                value={stockView}
                                onChange={(value) =>
                                    setStockView(value as 'material' | 'product')
                                }
                                data={[
                                    { value: 'product', label: 'Finished goods' },
                                    // RM/PM, matching the batch register's toggle and the
                                    // vendor portal - one vocabulary on both sides.
                                    { value: 'material', label: 'RM/PM' },
                                ]}
                            />
                        }
                        enableDownload
                        maxHeight="auto"
                    />
                </>
            )}

            {tab === 'production' && (
                <DataSheet
                    data={displayedProductionRows}
                    columns={[
                        {
                            key: 'orderCode',
                            /*
                             * ONE COLUMN, TWO LEVELS — the same idiom the Production Orders grid
                             * uses for "Manufacturer / SKU". Grouped by SKU the product heads the
                             * group and the order codes sit under it.
                             */
                            header: isSkuGrouped ? 'Product / Order' : 'Order',
                            // Wider by SKU: the product name heads the group and the full
                            // order code sits under it, and neither fit in an order code's width.
                            width: isSkuGrouped ? 240 : 160,
                            sticky: true,
                        },
                        { key: 'code', header: 'Line', width: 140 },
                        {
                            // The SKU on a line; on an order row, how many of them it holds —
                            // and in SKU mode, how many orders want this product.
                            key: 'productName',
                            header: isSkuGrouped ? 'Orders' : 'Product',
                            // "3 orders" needs no room; a product name does.
                            width: isSkuGrouped ? 110 : 240,
                            // Filtering on "2 orders" answers nothing; the Product column it
                            // replaces is what people filtered, and that is sticky in this mode.
                            filterable: !isSkuGrouped,
                        },
                        {
                            /*
                             * BEFORE THE FIGURES, as on the brand's Production Orders grid: what
                             * state a row is in is read before how much of it there is.
                             *
                             * Same pill as the Work Orders tab, so a status reads the same
                             * wherever it appears. Filter and CSV keep the readable label
                             * rather than the raw enum.
                             */
                            key: 'status',
                            header: 'Status',
                            width: 140,
                            render: (value: string) => <WorkOrderStatusPill status={value} />,
                            valueAccessor: (row) =>
                                STATUS_LABEL[String(row.status)] ?? String(row.status),
                            csvValue: (value: string) => STATUS_LABEL[String(value)] ?? value,
                        },
                        {
                            /*
                             * PLACED, on the order row — and on its lines once they are filed
                             * under a product, where the order row is gone. Blank on a product
                             * row, which was not placed on one date.
                             */
                            key: 'orderDate',
                            header: 'Ordered on',
                            width: 120,
                            render: (value: string | null) => value ?? '',
                        },
                        {
                            /*
                             * THE ORDER'S VALUE ON AN ORDER ROW, THE LINE'S OWN ON A LINE — that
                             * SKU's quantity by its rate, tax in — and the lines' sum on a
                             * product row. Never the whole order's figure repeated under each
                             * SKU, which read as every line being worth the lot.
                             */
                            key: 'total',
                            header: 'Order value',
                            width: 130,
                            align: 'right',
                            // A dash is not a zero: an order raised before pricing existed has
                            // no value, one priced at nothing has 0.00.
                            render: (value: string | null, row) =>
                                value === null || value === undefined ? (
                                    ''
                                ) : (
                                    <>{formatMoney(Number(value), row.currency ?? 'INR')}</>
                                ),
                            valueAccessor: (row) => (row.total === null ? null : Number(row.total)),
                            csvValue: (value: string | null) => value ?? '',
                        },
                        { key: 'ordered', header: 'Ordered', width: 100, align: 'right' },
                        { key: 'produced', header: 'Manufactured', width: 120, align: 'right' },
                        {
                            key: 'qcPending',
                            header: 'QC pending',
                            width: 120,
                            align: 'right',
                            renderHeader: () => (
                                <Tooltip label="Manufactured but not yet ruled on, and still at the manufacturer site. It can still be dispatched - the brand inspects the load on arrival." multiline w={280} withArrow>
                                    <span>QC pending</span>
                                </Tooltip>
                            ),
                            render: (value: number) =>
                                value > 0 ? (
                                    // Worth a look rather than a blocker: unruled goods may go.
                                    <Text fz={13} c={semanticColors.warning.dark}>
                                        {value}
                                    </Text>
                                ) : (
                                    <Text fz={13} c={neutralColors.textMuted}>
                                        0
                                    </Text>
                                ),
                        },
                        {
                            /*
                             * A STAGE, NOT A HISTORY. Cumulative "ever passed QC" would overlap
                             * Shipped completely — a batch that passed and then went would sit in
                             * both, inviting a reader to add them. The batches register keeps the
                             * cumulative reading, where an On hand column tells the two apart.
                             *
                             * The tooltip carries what the name alone cannot: this is what is
                             * standing here, not everything ever cleared. Its neighbour is netted
                             * the same way — unruled stock may now be dispatched, so counting it
                             * cumulatively would double up with Shipped.
                             */
                            key: 'qcApproved',
                            header: 'QC approved',
                            width: 130,
                            align: 'right',
                            renderHeader: () => (
                                <Tooltip
                                    label="Passed QC and still at the manufacturer site. Once it ships it moves to Sent."
                                    multiline
                                    w={260}
                                    withArrow
                                >
                                    <span>QC approved</span>
                                </Tooltip>
                            ),
                        },
                        {
                            /*
                             * HOW MUCH OF IT HAS ACTUALLY REACHED THE BRAND. The row followed a
                             * job from asked to made and then stopped, so the obvious next
                             * question had to be answered on another screen.
                             *
                             * Counted from the per-batch movement rows, each naming their own
                             * work order — the same read the site's own job cards use, so the
                             * two sides cannot report different progress on one order.
                             */
                            key: 'dispatched',
                            header: 'Shipped',
                            width: 110,
                            align: 'right',
                            render: (value: number) =>
                                value > 0 ? (
                                    value
                                ) : (
                                    // A job nothing has shipped against is the normal state of a
                                    // fresh order, not something to pick out.
                                    <Text fz={13} c={neutralColors.textMuted}>
                                        0
                                    </Text>
                                ),
                        },
                        {
                            /*
                             * WHAT IS STILL OWED: ordered minus what has actually left.
                             *
                             * Measured at the gate, so a unit made and sitting here is still
                             * open — the same rule as the banner above, which is why the two
                             * reconcile rather than needing to be explained to each other.
                             */
                            key: 'openQuantity',
                            header: 'Open quantity',
                            width: 130,
                            align: 'right',
                            render: (value: number) =>
                                value > 0 ? (
                                    value
                                ) : (
                                    // Nothing owed is the finished state, not a figure to chase.
                                    <Text fz={13} c={neutralColors.textMuted}>
                                        0
                                    </Text>
                                ),
                        },
                        { key: 'dueDate', header: 'Due', width: 120 },
                        {
                            /*
                             * The same action the brand's Production Orders grid offers, here
                             * where a coordinator is already looking at one manufacturer's
                             * book. Blank once there is nothing to cancel, and never on a
                             * headerless work order's synthetic row, which has no id to act on.
                             */
                            key: 'cancel',
                            header: '',
                            width: 130,
                            align: 'right',
                            render: (_value: unknown, row: SiteProductionRow) => {
                                const action = resolveCancelAction(row);
                                const id =
                                    row.kind === 'order' ? row.productionOrderId : row.workOrderId;
                                if (!action.available || !id) return '';
                                return (
                                    <Button
                                        size="xs"
                                        variant={ButtonVariant.OUTLINED_DESTRUCTIVE}
                                        loading={cancellingKey === row.key}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            requestCancel({
                                                key: row.key,
                                                kind: row.kind,
                                                code: row.code || row.orderCode,
                                                id,
                                            });
                                        }}
                                    >
                                        {action.label}
                                    </Button>
                                );
                            },
                            csvValue: () => '',
                        },
                    ]}
                    title="Production"
                    subtitle={
                        ordersLoading
                            ? 'Loading…'
                            : `${displayedProductionRows.length} ${
                                  isSkuGrouped ? 'SKU' : 'order'
                              }${displayedProductionRows.length === 1 ? '' : 's'} · ${lineCount(
                                  displayedProductionRows,
                              )} of ${lineCount(productionRows)} lines${undatedOrders}`
                    }
                    getRowKey={(row) => row.key}
                    // The same expansion the brand's Production Orders grid uses, on the same
                    // component: a header row holds the sums of the lines underneath it,
                    // whichever way they are grouped.
                    getSubRows={(row) => row.lines}
                    /*
                     * Matches the site's own Production screen, which already had this.
                     *
                     * ORDER ROWS ONLY, as everywhere else in the grid: children follow their
                     * parent rather than being ranked against it, so this finds an order by
                     * its code, not by a SKU sitting on one of its lines. The product column's
                     * own header filter is the way to that.
                     */
                    enableSearch
                    searchPlaceholder="Order or product…"
                    /*
                     * A NAMED CONTROL, not only the filter already sitting in the column header.
                     *
                     * Every column here can be filtered from its own header, status included, and
                     * that stays. But this is the cut people come to this tab to make - show me
                     * what is still open, show me what has shipped - and a question asked that
                     * often should not be hidden behind an icon. Matches the Purchase Orders
                     * toolbar, where the same question is asked the same way.
                     */
                    /*
                     * A NAMED CONTROL, not only the filter already sitting in the column
                     * header. Every column here can be filtered from its own header, status
                     * included, and that stays. But this is the cut people come to this tab to
                     * make — show me what is still open, show me what has shipped — and a
                     * question asked that often should not be hidden behind an icon.
                     *
                     * Withheld below two statuses: a picker that can only offer one thing can
                     * only do nothing.
                     */
                    filters={
                        productionStatusOptions.length < 2 ? undefined : [statusFilterConfig]
                    }
                    toolbarControls={
                        <Group gap={8} wrap="nowrap">
                            {/*
                              * The same two words the site's home page uses, in the same control the
                              * Stock tab has for Finished goods / RM-PM: one setting with two positions,
                              * rather than a fold hidden behind a menu nobody opened.
                              */}
                            <SegmentedControl
                                size="xs"
                                value={productionGroupBy ?? 'order'}
                                onChange={(value) => setProductionGroupBy(value === 'sku' ? 'sku' : null)}
                                data={[
                                    { value: 'order', label: 'By order' },
                                    { value: 'sku', label: 'By SKU' },
                                ]}
                            />
                            <DateRangePicker
                                value={orderedRange}
                                onChange={setOrderedRange}
                                onReset={
                                    hasDayRange(orderedRange)
                                        ? () => setOrderedRange(NO_DAY_RANGE)
                                        : undefined
                                }
                                title="Filter by Ordered on"
                            />
                        </Group>
                    }
                    enableDownload
                    maxHeight="auto"
                    emptyState={
                        <Text fz={13} c={neutralColors.textSecondary}>
                            {productionStatus.length
                                ? `Nothing at this site is ${productionStatus
                                      .map((status) =>
                                          (STATUS_LABEL[status] ?? status).toLowerCase(),
                                      )
                                      .join(' or ')}.`
                                : 'Nothing issued to this site yet. Work orders appear here with their per-line progress.'}
                        </Text>
                    }
                />
            )}

            {/*
              * THE LEDGER, NOT A PICKING SURFACE. It used to carry the shipment checkboxes
              * too, but everything tickable here appears in Dispatch by construction (both
              * are gated by canShip) — and picking from a newest-first list with shipped
              * rows mixed in is exactly the order FEFO exists to prevent. Sending lives on
              * the Dispatch tab; this one is for reconciling what the site said happened.
              */}
            {tab === 'batches' && (
                <BatchRegister
                    rows={register?.batches ?? []}
                    isLoading={registerLoading}
                    itemType={registerKind}
                    onKindChange={setRegisterKind}
                    initialQcFilter={qcFilter}
                    onClearQcFilter={() => setQcFilter(null)}
                    onSelectBatch={setSelectedBatch}
                />
            )}

            {/*
              * THE VERDICT WORKLIST - the same register, cut to what still awaits a QC
              * verdict, either kind. No new machinery: the pending filter, the panel a row
              * click opens (with its Pass / Fail), and the refetch a verdict triggers all
              * belong to the Batches tab already, so a judged batch simply disappears.
              * No onClearQcFilter on purpose: the tab IS the filter, so there is nothing
              * to clear out of it.
              */}
            {tab === 'qc' && (
                <BatchRegister
                    rows={register?.batches ?? []}
                    isLoading={registerLoading}
                    itemType={registerKind}
                    onKindChange={setRegisterKind}
                    initialQcFilter="PENDING"
                    title="QC Pending"
                    onSelectBatch={setSelectedBatch}
                />
            )}

            {tab === 'dispatch' &&
                /*
                 * TWO GRAINS, ONE QUEUE — the same pair the site's own screen shows, because
                 * the brand recording on a site's behalf is doing the site's job and should
                 * not be doing it through a different lens.
                 *
                 * NO NAME NEEDED FIRST. Every other write on this screen records something the
                 * site did and asks who there said it; releasing finished goods is the brand's
                 * own act, so there is nobody to name.
                 */
                (dispatchView === 'product' ? (
                    <ProductDispatch
                        groups={groups}
                        isLoading={registerLoading}
                        title="Dispatch"
                        subtitle={
                            registerLoading
                                ? 'Loading…'
                                : `${groups.length} product${groups.length === 1 ? '' : 's'} awaiting dispatch, soonest expiry first. Awaiting QC can go too — the brand checks the load on arrival.`
                        }
                        view={dispatchView}
                        onViewChange={setDispatchView}
                        isPicked={isProductPicked}
                        onToggle={toggleProduct}
                        onToggleAll={toggleAllProducts}
                    />
                ) : (
                    <BatchRegister
                        rows={queue}
                        isLoading={registerLoading}
                        itemType="product"
                        title="Dispatch"
                        subtitle={
                            registerLoading
                                ? 'Loading…'
                                : `${queue.length} batch${queue.length === 1 ? '' : 'es'} awaiting dispatch, soonest expiry first. Awaiting QC can go too — the brand checks the load on arrival.`
                        }
                        onSelectBatch={setSelectedBatch}
                        toolbarExtra={
                            <DispatchViewToggle
                                view={dispatchView}
                                onChange={setDispatchView}
                            />
                        }
                        selection={selection}
                    />
                ))}

            {/*
              * THE BRAND'S OTHER QUEUE. A site names no supplier; where a material has more
              * than one mapped, the receipt sits here until somebody says. Nothing waits on it
              * - the stock is usable, the batch traceable - which is why it is a banner and a
              * filter rather than a gate.
              */}
            {tab === 'history' && awaitingSupplierRows.length > 0 && (
                <Group justify="space-between" className={pageStyles.awaitingSupplier}>
                    <Text fz={13} c={neutralColors.textPrimary}>
                        <Text span fw={600}>
                            {awaitingSupplierRows.length} receipt
                            {awaitingSupplierRows.length === 1 ? '' : 's'} waiting for a supplier.
                        </Text>{' '}
                        These materials have more than one mapped supplier, so the site could
                        not be asked. Pick who sent each one - the reference the site typed
                        usually says.
                    </Text>
                    <Button
                        size="xs"
                        variant={ButtonVariant.DEFAULT}
                        onClick={() => setOnlyAwaitingSupplier((current) => !current)}
                    >
                        {showOnlyAwaiting ? 'Show all movements' : 'Show only these'}
                    </Button>
                </Group>
            )}

            {tab === 'history' && (
                <DataSheet<LedgerRow>
                    data={showOnlyAwaiting ? awaitingSupplierRows : ledgerRows}
                    columns={ledgerColumns}
                    title={showOnlyAwaiting ? 'Receipts waiting for a supplier' : 'Movements'}
                    subtitle={ledgerLoading ? 'Loading…' : `${ledgerData?.total ?? 0} recorded`}
                    getRowKey={(row) => row.key}
                    getSubRows={(row) => row.children}
                    enableSearch
                    enableDownload
                    maxHeight="auto"
                />
            )}

            {canSeeCost && (
                <ReceiptCostModal
                    receipt={costing}
                    onClose={() => setCosting(null)}
                    onSaved={() => void refetchLedger()}
                />
            )}

            <BatchPanel
                batch={selectedBatch}
                onClose={() => setSelectedBatch(null)}
                // The site, not the reporter. A verdict is the brand's own, so it needs no
                // "who told you" — and gating it on that hid the buttons behind an unrelated
                // field.
                brandSite={{ warehouseId }}
                // Correcting happens in the panel itself: it is already a form, and a second
                // one over it put two quantity fields and two reasons on screen at once.
                canAdjust
                onAdjusted={() => void refetchStock()}
                onChanged={() => void refetchRegister()}
                onHistory={() => {
                    if (!selectedBatch) return;
                    setHistoryBatchId(selectedBatch.batchId);
                    setSelectedBatch(null);
                }}
            />


            {/*
              * The site's own open lines, so a run recorded here can name the order it answers.
              * Without them the only choice on offer was "To stock", and everything a brand
              * entered on a site's behalf landed unattributed - the order it was phoned in
              * against went on reading zero made, however many runs were typed.
              *
              * Not restricted to one order, unlike the site's own order card: this is reached
              * from the site, so all of its orders are live answers. Restricted to the
              * products those orders ask for, though - the same rule as the site's own
              * portal, because a wrong product is a wrong product whoever keys it in.
              */}
            <DeclareBatchesModal
                opened={declaringMany}
                onClose={() => setDeclaringMany(false)}
                onDeclared={() => void refetchLedger()}
                orderLines={toOrderLines(ordersData?.orders ?? [])}
                restrictProductsToOrder
                // The escape hatch stays with the brand: it may still record a run to stock
                // on a site's behalf, exactly as it is exempt from the product cap. The
                // site's own grid no longer offers stock at all.
                allowStock
                onRecordInward={() =>
                    showToastError({
                        title: 'Record the delivery first',
                        subtitle:
                            'Use Receive on this site — the material has to be on the books before a run can consume it.',
                    })
                }
                onReleaseQc={() => setTab('stock')}
                onBehalfOf={onBehalfOf}
                reportedBy={reportedBy}
                onReportedByChange={setReportedBy}
            />

            <BatchDetailPanel
                item={stockPanelItem}
                onClose={() => setStockPanelItem(null)}
                readOnly
                onAdjust={(line) =>
                    stockPanelItem &&
                    // The no-batch line is the item itself, not a batch to pin the form to.
                    setAdjusting({ item: stockPanelItem, batch: line.batchId ? line : null })
                }
                /*
                 * Read-only, so nothing in the drawer can change a figure — but the row it
                 * was opened from may still go stale behind it if a verdict lands elsewhere,
                 * so a refetch on change costs nothing and keeps the contract honest.
                 */
                onChanged={() => void refetchStock()}
                onHistory={(line) => {
                    setHistoryBatchId(line.batchId);
                    setStockPanelItem(null);
                }}
            />

            <TraceDrawer
                batchId={historyBatchId}
                asSite={false}
                onClose={() => setHistoryBatchId(null)}
            />

            <AdjustStockModal
                warehouseId={warehouseId}
                item={adjusting?.item ?? null}
                batch={adjusting?.batch}
                batchRequired={isBatchRequired(adjusting?.item ?? null)}
                onClose={() => setAdjusting(null)}
                onAdjusted={() => {
                    // The figure the drawer is showing just moved, so both it and the grid
                    // behind it have to be re-read.
                    void refetchStock().then((res) => {
                        if (!stockPanelItem) return;
                        setStockPanelItem(
                            res.data?.items.find(
                                (candidate) => candidate.itemId === stockPanelItem.itemId,
                            ) ?? null,
                        );
                    });
                }}
            />

            <InviteLoginModal
                opened={isInviting}
                onClose={() => setIsInviting(false)}
                warehouseId={warehouseId}
                siteName={site?.name ?? 'this site'}
            />

            <ShipmentBuilderModal
                batches={building ? pickedBatches : []}
                groupByProduct={dispatchView === 'product'}
                onClose={() => setBuilding(false)}
                onShipped={() => {
                    // The register, because every picked row's remaining just dropped; the
                    // ledger, because the movements just landed on it.
                    clearPicked();
                    void refetchRegister();
                    void refetchLedger();
                }}
                onBehalfOf={onBehalfOf}
            />

            {tab === 'receive' && (
                <>
                    {/*
                      * The manufacturer's own grid, reused whole. A second copy for the brand
                      * would drift, and the day it did the brand could key something its own
                      * site could not. It asks for the reporter in its own "Entered by" field,
                      * so there is nothing to gate on here.
                      */}
                    <VendorReceiveContainer onBehalfOf={onBehalfOf} embedded />
                </>
            )}

            <Modal
                opened={!!reversing}
                onClose={() => setReversing(null)}
                centered
                size="md"
                title={
                    <Text fz={16} fw={600} c={neutralColors.textPrimary}>
                        Reverse this entry
                    </Text>
                }
            >
                <Stack gap={16}>
                    <Text fz={13} c={neutralColors.textSecondary}>
                        {reversing?.label} · {reversing?.itemId} · {reversing?.quantity}
                    </Text>

                    <Text fz={13} c={neutralColors.textPrimary}>
                        This appears in the site&apos;s own history as your reversal, with the
                        reason you give. It is never silent.
                    </Text>

                    <Textarea
                        label="Why"
                        // Mandatory here and optional for the site's own undo, because the two
                        // are different acts: they are fixing a typo, you are overruling them.
                        placeholder="Counted twice, wrong batch, reported in error…"
                        value={reverseReason}
                        onChange={(e) => setReverseReason(e.currentTarget.value)}
                        minRows={3}
                        autosize
                        required
                    />

                    {/*
                      * Undoing is a write like any other, so it names its reporter the same
                      * way the forms do rather than pointing at a banner elsewhere.
                      */}
                    <ReportedByField value={reportedBy} onChange={setReportedBy} />

                    <Group justify="flex-end" gap={12}>
                        <Button variant={ButtonVariant.DEFAULT} onClick={() => setReversing(null)}>
                            Cancel
                        </Button>
                        <Button
                            variant={ButtonVariant.FILLED_DESTRUCTIVE}
                            onClick={handleReverse}
                            disabled={!reverseReason.trim() || !reportedBy.trim() || isReversing}
                            loading={isReversing}
                        >
                            Reverse it
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </div>
    );
};

export default SiteDetailContainer;
